import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { PolicyModel, PolicyToUpdateModel, SensitiveLabelConfig } from 'src/app/models/api/governance/policy.model';
import { IntegrationModel } from 'src/app/models/api';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { SensitiveLabelsPlatform } from 'src/app/models/api/governance/sensitive-label.model';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { SensitiveLabelViewModel } from 'src/app/models/viewModels/sensitiveLabelViewModel.interface';
import { MatDialog } from '@angular/material/dialog';
import { InactiveIntegrationDialogComponent } from '../inactive-integration-dialog/inactive-integration-dialog.component';
import { SensitiveLabelsTableColumns } from 'src/app/models/tableColumns/sensitiveLabelsColumns.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { CheckboxClicked } from '../table-row/table-row.component';
import { PaginationOperationEnum } from '../table/table.component';
import _ from 'lodash';

interface LabelsState {
  selectedLabels: Map<string, SensitiveLabelConfig>;
}

@Component({
  selector: 'app-policy-sensitive-labels',
  templateUrl: './policy-sensitive-labels.component.html',
  styleUrls: ['./policy-sensitive-labels.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PolicySensitiveLabelsComponent implements OnInit, OnDestroy {
  @Input() public editable = true;
  @Input() public integrationModel: IntegrationModel;
  @Input() public policyModel: PolicyModel;
  @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  public changesWereDone = false;
  public noData = signal<boolean>(false);
  public hasError = signal<boolean>(false);

  // table config
  public sensitiveLabelsTableCols = SensitiveLabelsTableColumns;
  public tableRows: Observable<BehaviorSubject<SensitiveLabelViewModel>[]>;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;

  // labels names for search
  public labelsDisplayNamesForSearch: string[] = [];
  public labelsDisplayNamesToPresent: string[] = [];

  // loading behavior subject
  public isLoading = signal<boolean>(false);
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();

  private _pageNumber = 1;
  private readonly _pageSize = 50;
  private readonly _platform = SensitiveLabelsPlatform.mip; // First phase: MIP only
  private _allLabels: { id: string; displayName: string }[] = [];

  // label selection state
  private readonly _currentState: LabelsState = {
    selectedLabels: new Map(),
  };

  private readonly _originalState: LabelsState = {
    selectedLabels: new Map(),
  };

  // Subscriptions
  private _policyConfigUpdateSubscription?: Subscription;
  private _policyEntitySubscription?: Subscription;
  private _sensitiveLabelsSubscription?: Subscription;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {}

  ngOnInit(): void {
    this.isLoading.set(true);
    this._loadSensitiveLabels();
  }

  ngOnDestroy(): void {
    this._policyConfigUpdateSubscription?.unsubscribe();
    this._policyEntitySubscription?.unsubscribe();
    this._sensitiveLabelsSubscription?.unsubscribe();
  }

  public get selectedCount(): number {
    return this._currentState.selectedLabels.size;
  }

  public cancelSettings(): void {
    this.goBack.emit();
  }

  public resetForm(): void {
    this._resetCurrentState();
    this._buildLabelsViewModel();
    this._checkIfChangesWereDone();
    this.resetSearchBoxBehavioralSubject$.next(undefined);
  }

  public updatePolicyConfig(): void {
    this.isLoading.set(true);
    if (this.integrationModel.connectionStatus.code === StatusCodeEnum.off) {
      const dialogRef = this._matDialog.open(InactiveIntegrationDialogComponent, {
        data: this.integrationModel,
        disableClose: false,
        panelClass: 'dynamic-size-popup-dialog-container',
      });
      dialogRef.afterClosed().subscribe(() => {
        this._updatePolicyConfig();
      });
    } else {
      this._updatePolicyConfig();
    }
  }

  public onCheckboxClick(event: CheckboxClicked): void {
    const labelId: string = event.id;
    const status: boolean = event.status;

    if (status) {
      // Store label object at selection time
      const label = this._allLabels.find((l) => l.id === labelId);
      if (label) {
        this._currentState.selectedLabels.set(labelId, { id: labelId, name: label.displayName });
      }
    } else {
      this._currentState.selectedLabels.delete(labelId);
    }
    this._buildLabelsViewModel();
    this._checkIfChangesWereDone();
  }

  public labelSearched(labels: string[]): void {
    this.labelsDisplayNamesToPresent = labels;
    this._buildLabelsViewModel();
    this._handlePageInfo();
  }

  public onPagination(operation: PaginationOperationEnum): void {
    if (operation === PaginationOperationEnum.next) {
      this._pageNumber += 1;
    } else if (this._pageNumber > 1) {
      this._pageNumber -= 1;
    }
    this._handlePageInfo();
    this._buildLabelsViewModel();
  }

  private _initializeSearchComponent(): void {
    this.labelsDisplayNamesForSearch = [];
    this._allLabels.forEach((label) => {
      this.labelsDisplayNamesForSearch.push(label.displayName);
    });
    this.labelsDisplayNamesToPresent = _.cloneDeep(this.labelsDisplayNamesForSearch);
  }

  private _resetCurrentState(): void {
    this._currentState.selectedLabels = new Map(this._originalState.selectedLabels);
  }

  private _checkIfChangesWereDone(): void {
    const currentIds = new Set(this._currentState.selectedLabels.keys());
    const originalIds = new Set(this._originalState.selectedLabels.keys());

    if (_.isEqual(currentIds, originalIds)) {
      this.changesWereDone = false;
    } else {
      this.changesWereDone = true;
    }
    this.notifyChanges.emit(this.changesWereDone);
  }

  private _saveLabelsState(): void {
    const platformLabels = this.policyModel.config.sensitiveLabels?.[this._platform] || [];
    platformLabels.forEach((label: SensitiveLabelConfig) => {
      this._currentState.selectedLabels.set(label.id, label);
      this._originalState.selectedLabels.set(label.id, { ...label });
    });
  }

  private _removeStaleLabelsFromSelection(): void {
    const validLabelIds = new Set(this._allLabels.map((label) => label.id));

    // Remove any selected labels that don't exist in the API response
    const staleLabels: string[] = [];
    this._currentState.selectedLabels.forEach((_, labelId) => {
      if (!validLabelIds.has(labelId)) {
        staleLabels.push(labelId);
      }
    });

    // Remove stale labels from both current and original state
    staleLabels.forEach((labelId) => {
      this._currentState.selectedLabels.delete(labelId);
      this._originalState.selectedLabels.delete(labelId);
    });

    // Show notification if stale labels were removed
    if (staleLabels.length > 0) {
      this._notificationService.warning(
        `${staleLabels.length} label${staleLabels.length > 1 ? 's' : ''} no longer exist and will be removed on save.`
      );
    }
  }

  private _buildLabelsViewModel(): void {
    const viewModels: BehaviorSubject<SensitiveLabelViewModel>[] = [];

    this._allLabels.forEach((label) => {
      if (this._isLabelShouldBePresented(label)) {
        const isSelected = this._currentState.selectedLabels.has(label.id);

        const viewModel: SensitiveLabelViewModel = {
          rowId: label.id,
          clickable: false,
          id: label.id,
          displayName: label.displayName,
          checkboxStatus: isSelected,
          disabledCheckbox: !this.editable,
          disabled: false,
        } as SensitiveLabelViewModel;

        viewModels.push(new BehaviorSubject(viewModel));
      }
    });

    this.tableRows = new BehaviorSubject(viewModels);
  }

  private _initializePageInfo(): void {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this._pageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }

  private _handlePageInfo(): void {
    const totalItems = this.labelsDisplayNamesToPresent.length;
    const totalPages = Math.ceil(this.labelsDisplayNamesToPresent.length / this._pageSize);

    this.pageInfo$.next({
      number: this._pageNumber,
      currSize: this._pageNumber === totalPages ? totalItems % this._pageSize || this._pageSize : this._pageSize,
      maxSize: this._pageSize,
      totalItems: totalItems,
      totalPages: totalPages,
    });
  }

  private _isLabelShouldBePresented(label: { id: string; displayName: string }): boolean {
    const elementsOnPage = this.labelsDisplayNamesToPresent.slice(
      (this._pageNumber - 1) * this._pageSize,
      (this._pageNumber - 1) * this._pageSize + this._pageSize
    );
    return elementsOnPage.includes(label.displayName);
  }

  private _loadSensitiveLabels(): void {
    this._sensitiveLabelsSubscription = this._policiesHttpService
      .getSensitiveLabels(this.integrationModel.id)
      .subscribe({
        next: (response) => {
          const mipLabels = response.labels[SensitiveLabelsPlatform.mip] || [];
          this._allLabels = mipLabels.map((label) => ({
            id: label.id,
            displayName: label.name,
          }));

          // Load labels from policy into state
          this._saveLabelsState();

          // Remove stale labels from selection that don't exist in API response
          this._removeStaleLabelsFromSelection();

          this._initializeSearchComponent();
          this._initializePageInfo();
          this._buildLabelsViewModel();
          this._handlePageInfo();
          this.noData.set(this._allLabels.length === 0);
          this.hasError.set(false);
          this.isLoading.set(false);
        },
        error: (error) => {
          // Check if this is a 404 "no labels found" response with the specific message
          const isNoLabelsError =
            error?.status === 404 && error?.error?.detail?.includes('No sensitivity labels found for this integration');
          if (isNoLabelsError) {
            this._notificationService.info('No sensitivity labels found for this integration');
            this._allLabels = [];
            this._initializeSearchComponent();
            this._initializePageInfo();
            this._buildLabelsViewModel();
            this._handlePageInfo();
            this.noData.set(true);
            this.hasError.set(false);
            this.isLoading.set(false);
            return;
          }

          // Handle other errors
          this._httpErrorHandlerService.handle(error);
          this._notificationService.error('Failed to load available labels. Showing currently selected labels only.');
          this.hasError.set(true);

          // Show selected labels from policy even on API error
          const platformLabels = this.policyModel.config.sensitiveLabels?.[this._platform] || [];
          this._allLabels = platformLabels.map((label: SensitiveLabelConfig) => ({
            id: label.id,
            displayName: label.name,
          }));

          this._saveLabelsState();
          this._initializeSearchComponent();
          this._initializePageInfo();
          this._buildLabelsViewModel();
          this._handlePageInfo();
          this.noData.set(platformLabels.length === 0);
          this.isLoading.set(false);
        },
      });
  }

  private _updatePolicyConfig(): void {
    this.isLoading.set(true);

    this._policyConfigUpdateSubscription = this._policiesHttpService
      .updatePolicy(this.integrationModel.id, this.policyModel.id, this._generateUpdatePolicyModel())
      .subscribe({
        next: () => {
          this._notificationService.success('Policy updated successfully');
          this._policyEntitySubscription = this._policiesHttpService
            .getPolicy(this.integrationModel.id, this.policyModel.id)
            .subscribe({
              next: (response) => {
                this.policyModel = response;
                this.policyUpdated.emit(this.policyModel);
                this._resetAllStates();
                this._saveLabelsState();
                this._buildLabelsViewModel();
                this._checkIfChangesWereDone();
                this.isLoading.set(false);
              },
              error: (error) => {
                this._httpErrorHandlerService.handle(error);
                this.isLoading.set(false);
              },
            });
        },
        error: (error) => {
          this._httpErrorHandlerService.handle(error);
          this.isLoading.set(false);
        },
      });
  }

  private _resetAllStates(): void {
    this._originalState.selectedLabels = new Map();
    this._currentState.selectedLabels = new Map();
  }

  private _generateUpdatePolicyModel(): PolicyToUpdateModel {
    const config = _.cloneDeep(this.policyModel.config);
    if (!config.sensitiveLabels) {
      config.sensitiveLabels = {};
    }

    config.sensitiveLabels[this._platform] = Array.from(this._currentState.selectedLabels.values());

    return {
      name: this.policyModel.name,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : this.policyModel.type,
      config: config,
    } as PolicyToUpdateModel;
  }
}
